import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGoogleCalendarSourceSettingsText,
  useGoogleCalendarSourceSettingsViewModel,
} from "@onetasklist/shared/sources/googlecalendar";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GoogleCalendarSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GoogleCalendarSourceSettings: React.FC<
  GoogleCalendarSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useGoogleCalendarSourceSettingsText(t);
  const vm = useGoogleCalendarSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [calendarsQuery, setCalendarsQuery] = useState("");
  const [colourQuery, setColourQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.googlecalendar.sectionCalendar",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.calendarsLabelText}
        hint={text.calendarsHintText}
      >
        <SourceMultiTagPicker
          selectedIds={vm.selectedCalendarIds}
          query={calendarsQuery}
          placeholder={text.calendarsPlaceholderText}
          options={vm.calendars.map((calendar) => ({
            id: calendar.id,
            label: calendar.summary ?? calendar.id,
          }))}
          onQueryChange={setCalendarsQuery}
          onSelectionChange={vm.onSelectCalendars}
          minQueryLength={0}
          typeToSearchText={text.calendarsHintText}
          isLoading={!vm.calendarsLoaded}
        />
        {vm.hasError && <Text>{text.loadErrorText}</Text>}
      </Field>
      <Field
        orientation="horizontal"
        label={text.colourLabelText}
        hint={text.colourHintText}
      >
        <SourceSingleTagPicker
          selectedId={vm.selectedColourId}
          selectedLabel={
            vm.selectedColourId ? text.colourNames[vm.selectedColourId] : undefined
          }
          query={colourQuery}
          placeholder={text.colourNotSetText}
          options={vm.colourIds.map((id) => ({
            id,
            label: text.colourNames[id],
          }))}
          onQueryChange={setColourQuery}
          onSelect={vm.onSelectColour}
          clearSelectionText={text.colourNotSetText}
        />
      </Field>
    </SettingsSectionCard>
  );
};
